import * as Sentry from '@sentry/browser';
import { useAuth0 } from '@auth0/auth0-react';
import React, { useCallback, useState } from 'react';
import { Button, TextField, DialogActions, DialogContent } from '@mui/material';
import {
  AllUserGroupsForUserManagementDocument,
  useCreateUserGroupMutation,
  UsersByUserGroupDocument,
} from '@/__generated__/graphql';
import { containedButtonStyle, textButtonStyle } from '@/theme';
import { GenericModal } from '@/components/shared/GenericModal';
import { dialogOverlay } from '../../styles';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';

type Props = {
  open: boolean;
  handleToggle: () => void;
};

export const AddGroupDialog = ({ open, handleToggle }: Props) => {
  const { user } = useAuth0();
  const [groupName, setGroupName] = useState('');
  const [errorMessage, setErrorMessage] = useState(false);

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  const handleClose = useCallback(() => {
    handleToggle();
    setGroupName('');
    setErrorMessage(false);
  }, [handleToggle]);

  const [createUserGroupMutation] = useCreateUserGroupMutation();
  const handleCreateGroup = useCallback(() => {
    if (groupName) {
      createUserGroupMutation({
        variables: {
          input: {
            name: groupName,
            userIds: undefined,
          },
        },
        refetchQueries: [AllUserGroupsForUserManagementDocument, UsersByUserGroupDocument],
        onCompleted: (response) => {
          if (response.createUserGroup) {
            handleClose();
            showSnackBar(`The group has been created successfully`, 'success');
          }
        },
        onError: (error) => {
          Sentry.captureMessage(`Failed to create user group: ${error.message}`, {
            user: { sub: user?.sub },
            extra: {
              groupName: groupName,
            },
          });
          showSnackBar(`Failed to create user group: ${error.message}`, 'error');
        },
      }).catch(() => {
        setErrorMessage(true);
      });
    }
  }, [createUserGroupMutation, groupName, handleClose, showSnackBar, user?.sub]);

  return (
    <GenericModal
      open={open}
      handleClose={handleClose}
      title="Create Group"
      headerStyleProp={{ mb: '5%' }}
      crossIconStyle={{ backgroundColor: 'transparent', color: 'primary' }}
      sx={dialogOverlay}
    >
      <DialogContent sx={{ fontSize: '1.4rem', color: 'primary.main' }}>
        <TextField
          label="Group Name"
          variant="standard"
          sx={{ width: '100%' }}
          helperText={errorMessage ? 'Group name already exists' : ' '}
          error={!!errorMessage}
          value={groupName}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setGroupName(event.target.value);
          }}
        />
      </DialogContent>

      <DialogActions sx={{ borderTop: 1, borderColor: 'divider', mt: 23, pt: 2 }}>
        <Button onClick={handleClose} variant="text" sx={textButtonStyle}>
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          sx={containedButtonStyle}
          onClick={handleCreateGroup}
          disabled={!groupName}
        >
          Create Group
        </Button>
      </DialogActions>
    </GenericModal>
  );
};
